import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function SmtpConfigDialog({ open, onOpenChange, sessionId, API }) {
  const [config, setConfig] = useState({ host: "", port: 587, username: "", password: "", sender_email: "", admin_email: "", use_tls: true });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    axios.get(`${API}/smtp-config?session_id=${sessionId}`).then((res) => {
      if (res.data.configured) {
        setConfig({
          host: res.data.host || "",
          port: res.data.port || 587,
          username: res.data.username || "",
          password: res.data.password || "",
          sender_email: res.data.sender_email || "",
          admin_email: res.data.admin_email || "",
          use_tls: res.data.use_tls !== false,
        });
      }
    }).catch(() => {});
  }, [open, sessionId, API]);

  const handleSave = async () => {
    if (!config.host || !config.username || !config.password || !config.sender_email) {
      setMessage({ type: "error", text: "Bitte alle Pflichtfelder ausfüllen" });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      await axios.put(`${API}/smtp-config?session_id=${sessionId}`, config);
      onOpenChange(false);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Fehler beim Speichern" });
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!config.host || !config.username || !config.password) {
      setMessage({ type: "error", text: "Bitte Host, Benutzername und Passwort ausfüllen" });
      return;
    }
    setTesting(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/smtp-config/test?session_id=${sessionId}`, config);
      setMessage({ type: res.data.success ? "success" : "error", text: res.data.message });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Verbindungstest fehlgeschlagen" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>SMTP-Konfiguration</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">SMTP Host *</label>
            <Input data-testid="smtp-host" value={config.host} onChange={(e) => setConfig({ ...config, host: e.target.value })} placeholder="smtp.gmail.com" />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground">Port *</label>
              <Input data-testid="smtp-port" type="number" value={config.port} onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) || 587 })} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={config.use_tls} onChange={(e) => setConfig({ ...config, use_tls: e.target.checked })} className="rounded" />
                TLS
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Benutzername *</label>
            <Input data-testid="smtp-username" value={config.username} onChange={(e) => setConfig({ ...config, username: e.target.value })} placeholder="user@example.com" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Passwort *</label>
            <Input data-testid="smtp-password" type="password" value={config.password} onChange={(e) => setConfig({ ...config, password: e.target.value })} placeholder="SMTP-Passwort" />
          </div>
          <div className="border-t border-border pt-3">
            <label className="text-xs font-medium text-muted-foreground">Absender-Adresse *</label>
            <Input data-testid="smtp-sender" value={config.sender_email} onChange={(e) => setConfig({ ...config, sender_email: e.target.value })} placeholder="noreply@example.com" />
          </div>
          {message && (
            <div data-testid="smtp-message" className={`text-xs p-2 rounded ${message.type === "success" ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-destructive/10 text-destructive"}`}>
              {message.text}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button data-testid="smtp-test-btn" variant="outline" className="flex-1" onClick={handleTest} disabled={testing || loading}>
              {testing ? "Teste..." : "Verbindung testen"}
            </Button>
            <Button data-testid="smtp-save-btn" className="flex-1" onClick={handleSave} disabled={loading || testing}>
              {loading ? "Speichere..." : "Speichern"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
